/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { login, isLoggedIn, pending2FA, logout} from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION, IVR_HOSTS } from '../../Constants';
import { useTranslation } from 'react-i18next';
import {apiErrorMessage, checkValidToken, getLang, landOrResolve, decodeRedirectUrl} from "../../Helper"
import cookie from "js-cookie";
import { isCellularLogin, isFeatureOn } from "../../portalSettings";
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useCaptcha } from "../../captcha";
import { useEnterSubmit } from "../../hooks/useEnterSubmit";
import ClassicLoginLayout from "../login/ClassicLoginLayout";
import CellularLoginLayout from "../login/CellularLoginLayout";

/**
 * מסך ההתחברות - **אחד**, בשתי ערכות עיצוב.
 *
 * הקובץ הזה הוא ההתחברות עצמה: המצב, הבדיקות, הפנייה לשרת וההכרעה לאן ממשיכים.
 * מה שמצויר על המסך יושב בשתי פריסות (`ClassicLoginLayout`, `CellularLoginLayout`),
 * והן מקבלות את כולו כאובייקט אחד ואינן יודעות דבר על השרת. ההפרדה היא בכוונה לפי
 * הקו הזה: כל מה ש**אינו** בחוזה הביניים אינו יכול להיפרד בין שתי הערכות.
 *
 * עד כה אלה היו שני רכיבים - `Login` ו-`LoginSimCustomers` - עם שני מימושים לאותה
 * התחברות, וההפרש ביניהם כבר הצטבר: אחד המשיך ב-`landAfterLogin` והשני ב-
 * `landOrResolve`, אחד הציג שגיאה בשורה והשני בחלון, ושלושת הענפים שאחרי הסיסמה
 * נכתבו פעמיים. זו בדיוק הסיבה שכל שאר המסכים אינם משוכפלים אלא מקבלים עור
 * (`stylesSim.css`, הבלוק של `data-portal-skeleton="cellular"`).
 *
 * הבחירה בין הפריסות היא `isCellularLogin` - אותה תשובה בדיוק שנכתבת על `<html>`
 * וקובעת את העור של שאר המסכים, ולא הכרעת ניתוב משלה. ריסיילר שאינו סלולרי, או
 * תמונת הגדרות שנכשלה, מקבל את הפריסה המלאה.
 */
export function Login() {
    const [state, setState] = useState({ username: "", password: "", errors: {}, passwordForm: false });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isUserLoggedIn = useSelector(isLoggedIn);
    const reseller = useSelector((state) => state.reseller.value);

    /** איזו פריסה מצוירת. אותה הכרעה של העור, ולא שדה שני שיסתור אותה. */
    const cellular = isCellularLogin(reseller);

    // One login attempt at a time: a second click sends the password again and
    // counts as another failed-attempt slot against the account.
    const submit = useSubmitGuard();
    /**
     * הקאפצ'ה. פעילה רק כשההגדרה הגיעה מהשרת, ומה שנשלח נבדק שם בלי קשר למסך.
     */
    const captcha = useCaptcha('login');

    /**
     * שחזור שם משתמש וסיסמה, והרשמה - לפי ההגדרה של הריסיילר.
     *
     * **המסך אינו השער.** `sendResetPasswordEmail`, `resendUsernameByEmail` ומסלול
     * ההרשמה נבדקים בשרת לפי ההגדרה, גם אם הקישור מוצג ואפילו אם הפנייה לא באה
     * מהמסך הזה בכלל. מה שנקבע כאן הוא רק אם להציע את הפעולה - ולכן זו אותה שאלה
     * בשתי הפריסות, ולא כלל לכל אחת.
     */
    const showRestoreUsername = isFeatureOn(reseller, 'allowUsernameRecovery');
    const showRestorePassword = isFeatureOn(reseller, 'allowPasswordReset');
    const showRegister = isFeatureOn(reseller, 'allowRegistration');

    /**
     * ההתחברות המהירה - **מצב בתוך המסך, ולא נתיב משלה.**
     *
     * הבחירה בין מהיר לסיסמה היא ציר שני, אורתוגונלי לערכת העיצוב. נתיב לכל צירוף
     * היה ארבעה מסכים במקום אחד, ושני עותקים של אותה זרימה שיסתרו זה את זה.
     *
     * **המסך אינו השער.** `quickLoginStart` נבדק בשרת מול ההגדרה בלי קשר למה שמצויר
     * כאן; מה שנקבע כאן הוא רק מה נפתח ראשון.
     */
    const quickLoginAvailable = isFeatureOn(reseller, 'allowQuickLogin');
    const quickLoginIsDefault = quickLoginAvailable && isFeatureOn(reseller, 'quickLoginIsDefault');
    const [quickMode, setQuickMode] = useState(quickLoginIsDefault);
    // הקאפצ'ה של ההתחברות המהירה - פעולה משלה, ולא זו של הסיסמה.
    const quickCaptcha = useCaptcha('quickLogin');

    /**
     * ההודעה שהתנתקות הותירה אחריה - **בשורה, בשתי הפריסות.**
     *
     * הפריסה הסלולרית הציגה אותה בחלון קופץ והמלאה בשורה מתחת לטופס, כלומר שני
     * מסלולי שגיאה לאותה הודעה. השורה נבחרה כי היא זו שנשארת על המסך בזמן שמתקנים
     * את הטופס, ובגללה גם אין צורך ב-`showErrorDialog` במסך הזה כלל.
     */
    useEffect(() => {
        let errorMessage = cookie.get('logout_error');
        if(errorMessage !== "null" && (typeof errorMessage !== 'undefined')){
            setState((prev) => ({...prev, errors: {server_error: errorMessage}}));
            cookie.set('logout_error', "null", { sameSite: 'strict' });
        }
        // eslint-disable-next-line
      }, []);

    useEffect(()=>{
        if(isUserLoggedIn){
            checkValidToken(navigate, dispatch, logout);
        }
        // eslint-disable-next-line
    },[]);

    /**
     * הרקע הלבן של הפריסה הסלולרית, ו**הניקוי שלו ביציאה**.
     *
     * הפריסה הזו אינה מצוירת בתוך `Frame`, ולכן היא צובעת את הגוף עצמו. בלי הניקוי
     * הצבע היה נשאר על כל מסך שמנווטים אליו מכאן - וזה בדיוק מה שקרה עד היום, כי
     * המסך הסלולרי היה נקודת הכניסה של הזרימה כולה.
     */
    useEffect(() => {
        if (!cellular) return undefined;
        const previous = document.body.style.background;
        document.body.style.background = "white";
        return () => { document.body.style.background = previous; };
    }, [cellular]);

    const handleChange = (e) => {
        setState((prev) => ({...prev, [e.target.id]: e.target.value, errors: {}}));
    };

    /**
     * השלב הראשון של הפריסה המלאה - שם משתמש, ואז סיסמה.
     *
     * הפריסה הסלולרית אינה קוראת לזה: שני השדות מוצגים בה יחד, וזה חלק מהעיצוב
     * שלה. `passwordForm` נשאר `false` שם, ולכן `handleSubmit` אינו נשען עליו.
     */
    const handleSubmitUsername = async (e) => {
        e.preventDefault();
        if(state.username === "") {
            setState((prev) => ({...prev, errors: {username: 'Username is required'}}));
            return;
        }
        setState((prev) => ({...prev, passwordForm: true, errors: {}}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // שני השדות, ובסדר הזה. הפריסה הסלולרית שולחת את שניהם במכה אחת, ולכן
        // הבדיקה על שם המשתמש נדרשת גם כאן ולא רק בשלב שלפניו.
        if(state.username === "") {
            setState((prev) => ({...prev, errors: {username: 'Username is required'}}));
            return;
        }
        if(state.password === "") {
            setState((prev) => ({...prev, errors: {password: 'Password is required'}}));
            return;
        }

        setState((prev) => ({...prev, errors: {}}));
        let errorMessage = t(DEFAULT_ERROR_TEXT);

        /**
         * `withCredentials` כדי שעוגיית המכשיר המזוהה תעבור בקריאה הזו.
         *
         * זה מה שמאפשר לשרת לדלג על קוד האימות בדפדפן שהלקוח סימן בו "זכור את
         * המכשיר", ובלעדיו הסימון היה תיבה שנשמרת ואינה עושה דבר.
         */
        const config = {'headers': {"lang": getLang()}, withCredentials: true};
        const data = {userName: state.username, password: state.password};

        // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
        const payload = await captcha.attach(data);
        // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
        if (!payload) {
            return;
        }

        try {
            const reply = await submit.run(() => axios.post(API_URL + 'login', payload, config));
            // A blocked second click returns nothing; the first one is still running.
            if (!reply) {
                return;
            }
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK
                    && reply.data.hasOwnProperty('token')){
                /**
                 * **סיסמה נכונה אינה סוף ההתחברות.**
                 *
                 * חשבון שיש לו אימות דו-שלבי מקבל טוקן שאינו פותח דבר, וסשן שחייב
                 * רישום שיטה או אימות ערוץ קשר נדחה בכל בקשה עד שהחובה הושלמה.
                 * להנחית את אלה בממשק פירושו לשלוח את הלקוח לדלת שתיטרק בפניו, ובמקרה
                 * של אימות ערוץ הקשר - ללופ סגור מול ה-interceptor.
                 *
                 * שתי החובות שאחרי הטוקן נפתרות ב-`landOrResolve`, ממקום אחד: ארבע
                 * נקודות הכניסה קראו ל-`landAfterLogin` ישירות, וכל שער חדש בשרת נדרש
                 * להיזכר בארבעתן.
                 */
                if(reply.data.requires2FA === true) {
                    dispatch(pending2FA({token: reply.data.token}));
                    navigate('/login-verify', {state: {
                        methodUuid: reply.data.hasOwnProperty('methodUuid') ? reply.data.methodUuid : "",
                        // Whether the code screen should offer "remember this device".
                        trustedDevicesEnabled: reply.data.trustedDevicesEnabled === true,
                    }});
                    return;
                }
                dispatch(login({token : reply.data.token}));
                landOrResolve(reply.data, navigate);
            }else{
                if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                    errorMessage = apiErrorMessage(reply.data, t);
                }
                setState((prev) => ({...prev, errors: {server_error: errorMessage}}));
            };
        } catch (e) {
            setState((prev) => ({...prev, errors: {server_error: errorMessage}}));
        }
    };

    const hasErrors = () => Object.keys(state.errors).length > 0;

    const handlePreviousClick = (e) => {
        e.preventDefault();
        setState((prev) => ({...prev, passwordForm: false, errors: {}}));
    };

    /**
     * חזרה למסך ההתחברות של המרכזייה, כשמשם הגיעו.
     *
     * רק מארח שברשימה (`IVR_HOSTS`) - הכתובת מגיעה מפרמטר ומעוגייה, ושתיהן נשלטות
     * ע"י מי ששולח את הקישור. בלי הבדיקה זה היה קישור פתוח לכל יעד מתוך מסך התחברות.
     */
    const getIvrLoginUrl = () => {
        const params = new URLSearchParams(window.location.search);
        let redirectFrom = params.get("redirect_from") || cookie.get("redirect_from");
        if(!redirectFrom) return null;
        redirectFrom = decodeRedirectUrl(redirectFrom);
        try {
            const url = new URL(redirectFrom);
            if(!IVR_HOSTS.includes(url.host)) return null;
            return url.origin + "/login" + url.search + url.hash;
        } catch {
            return null;
        }
    };

    const toggleShowPassword = (e) => {
        if(e.target.parentElement.querySelector('input')) {
            e.target.parentElement.querySelector('input').type = e.target.parentElement.querySelector('input').type === "password"?"text":"password";
        }
    };

    /**
     * Enter ממשיך לשלב הבא - **בפריסה המלאה בלבד.**
     *
     * שם הפעולה הראשית היא `div` ולא כפתור שליחה, ולכן הדפדפן אינו שולח דבר מעצמו:
     * שם משתמש מוביל לשלב הסיסמה, וסיסמה שולחת. בפריסה הסלולרית יש `type="submit"`
     * אמיתי בתוך הטופס - שם Enter כבר עובד, והוק נוסף היה מנסה להתחבר פעמיים.
     *
     * וגם לא במצב המהיר: `QuickLogin` הוא טפסים משלו, עם כפתורי שליחה משלהם.
     */
    useEnterSubmit(
        (e) => (state.passwordForm ? handleSubmit(e) : handleSubmitUsername(e)),
        !cellular && !quickMode && !submit.busy
    );

    /**
     * החוזה בין ההתחברות לבין מה שמצויר. אובייקט אחד, ושתי הפריסות מקבלות אותו כולו.
     */
    const view = {
        t, reseller, state, hasErrors,
        showRegister, showRestoreUsername, showRestorePassword,
        quickMode, setQuickMode, quickLoginAvailable, quickCaptcha,
        captcha, submit, navigate,
        ivrLoginUrl: getIvrLoginUrl(),
        handleChange, handleSubmit, handleSubmitUsername, handlePreviousClick, toggleShowPassword,
    };

    return cellular ? <CellularLoginLayout view={view} /> : <ClassicLoginLayout view={view} />;
}
